import { useEffect, useState, useCallback } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const PAGE_SIZE = 5;

function relativeDate(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(diffMs / day);
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? "s" : ""} ago`;
  return then.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function Stars({ rating, size = "h-4 w-4" }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={
            size + " " + (n <= rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30")
          }
        />
      ))}
    </div>
  );
}

export default function DoctorReviewsSection({ doctorId, rating, totalReviews }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(totalReviews ?? 0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(
    async (p, append) => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/doctors/${doctorId}/reviews?page=${p}&page_size=${PAGE_SIZE}`,
        );
        setItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
        setTotal(data.total ?? data.items?.length ?? 0);
        setHasMore(!!data.has_more);
        setPage(p);
      } finally {
        setLoading(false);
      }
    },
    [doctorId],
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const ratingDisplay =
    rating !== undefined && rating !== null ? Number(rating).toFixed(1) : "—";
  const reviewsCount = total ?? totalReviews ?? 0;

  return (
    <section
      data-testid="doctor-reviews-section"
      className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Patient reviews</h2>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
              <Star className="h-4 w-4 fill-current" />
              <span data-testid="reviews-avg-rating">{ratingDisplay}</span>
            </span>
            <span className="text-muted-foreground">
              ({<span data-testid="reviews-total-count">{reviewsCount}</span>}{" "}
              {reviewsCount === 1 ? "review" : "reviews"})
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {loading && items.length === 0 ? (
          <div className="text-sm text-muted-foreground">Loading reviews…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="reviews-empty-state"
            className="text-sm text-muted-foreground bg-secondary/40 rounded-2xl p-5"
          >
            No reviews yet. Be the first to share your experience after your consultation.
          </div>
        ) : (
          items.map((r) => (
            <article
              key={r.id}
              data-testid={`review-item-${r.id}`}
              className="border border-border rounded-2xl p-4"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-medium text-sm">{r.patient_name || "Anonymous"}</div>
                  <div className="mt-1">
                    <Stars rating={r.rating} />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">{relativeDate(r.created_at)}</div>
              </div>
              {r.comment && <p className="mt-3 text-sm text-foreground/90">{r.comment}</p>}
            </article>
          ))
        )}
      </div>

      {hasMore && (
        <div className="mt-5">
          <Button
            data-testid="reviews-show-more-btn"
            variant="outline"
            onClick={() => fetchPage(page + 1, true)}
            disabled={loading}
            className="rounded-full"
          >
            {loading ? "Loading…" : "Show more reviews"}
          </Button>
        </div>
      )}
    </section>
  );
}
